<template>
  <div v-if="show"
      :attr_disabled="disabled"
      class="base-cell">
    <!-- validationがないときは、処理速度向上のため、v-validateを書かない一時対応 -->
    <template v-if="needValidation">
      <template v-if="isTree">
        <el-tree
            :data="treeData"
            :expand-on-click-node="false"
            :node-key="properties.id || 'id'">
          <BaseTreeNodeRadio
              :id="data[properties.id]"
              v-validate="validationRule"
              slot-scope="{ node, data }"
              class="tree_node"
              :label="data[properties.label]"
              :data="data"
              :display-item="displayItem"
              :select-code="properties.selectCode"
              :site-code="siteCode"
              :disabled="disabled" />
        </el-tree>
      </template>
      <template v-else-if="isImage">
        <template v-if="properties.asyncGetImage">
          <ImageLoader
              :src="asyncImageSrc"
              :width="properties.imageWidth"
              :height="properties.imageHeight" />
        </template>
        <template v-else>
          <ImageLoader
              :src="imageUrl"
              :width="properties.imageWidth"
              :height="properties.imageHeight" />
        </template>
      </template>
      <template v-else-if="isHTML"
          lang="html">
        <div v-html="formattedDisplayValue" />
      </template>
      <template v-else-if="isHTMLFunction"
          lang="html">
        <div v-html="functionDisplayValue" />
      </template>
      <template v-else-if="isButton">
        <el-button
            ref="input"
            :size="size"
            :type="type"
            :disabled="disabledButton"
            :icon="icon"
            :class="buttonClass"
            @click="onButtonClick">
          {{ properties.label }}
        </el-button>
      </template>
      <template v-else-if="isSelectBox">
        <BaseValidSelect ref="input"
            v-validate="validationRule"
            :data-vv-name="uid"
            :data-vv-scope="validateScope"
            :data-vv-as="properties.validateAs"
            :error-msg="errorMessage"
            :popover-error-msg="properties.popoverErrorMsg"
            :name="name"
            :value="value"
            :filterable="properties.filterable"
            :options="selectOptions"
            :placeholder="properties.placeholder"
            :size="size"
            :multiple="properties.multiple"
            :clearable="properties.clearable"
            @input="onChange"
            @focus="onFocus"
            @blur="onBlur" />
      </template>
      <template v-else-if="isMultiSelectTag">
        <div class="multi-select-tag">
          <el-tag v-for="(tag, index) in multiSelectCodeNames"
              :key="index"
              type="info">
            {{ tag.name }}
          </el-tag>
        </div>
      </template>
      <template v-else-if="isRadioButton">
        <BaseValidRadio
            :name="name"
            :value="value"
            :radio-list="selectOptions"
            :disabled="disabled"
            button
            @input="onChange" />
      </template>
      <template v-else-if="isCheckBox">
        <BaseValidCheckbox
            ref="input"
            :name="name"
            :class="checkBoxClass"
            :value="value"
            :check-list="checkBoxOptions"
            :disabled="disabled"
            button
            @input="onChange" />
      </template>
      <template v-else-if="isDate">
        <BaseValidDatePicker ref="input"
            v-validate="validationRule"
            :data-vv-name="uid"
            :data-vv-scope="validateScope"
            :data-vv-as="properties.validateAs"
            :error-msg="errorMessage"
            :popover-error-msg="properties.popoverErrorMsg"
            :name="name"
            :value="value"
            :placeholder="properties.placeholder"
            :start-placeholder="properties.placeholder"
            :end-placeholder="properties.placeholder"
            :type="properties.elementType"
            @input="onChange"
            @focus="onFocus"
            @blur="onBlur" />
      </template>
      <template v-else-if="isInputNumber">
        <BaseValidNumber ref="input"
            v-validate="validationRule"
            :data-vv-name="uid"
            :data-vv-scope="validateScope"
            :data-vv-as="properties.validateAs"
            :error-msg="errorMessage"
            :popover-error-msg="properties.popoverErrorMsg"
            :name="name"
            :value="value"
            :placeholder="properties.placeholder"
            :precision="properties.precision"
            :size="size"
            @input="onChange"
            @focus="onFocus"
            @blur="onBlur" />
      </template>
      <template v-else-if="isTextArea">
        <BaseValidInput ref="input"
            v-validate="validationRule"
            :data-vv-name="uid"
            :data-vv-scope="validateScope"
            :data-vv-as="properties.validateAs"
            :error-msg="errorMessage"
            :popover-error-msg="properties.popoverErrorMsg"
            :name="name"
            :value="value"
            :placeholder="properties.placeholder"
            :rows="properties.rows"
            :auto-complete="properties.autoComplete"
            :autosize="properties.autosize"
            :disabled="disabled"
            type="textarea"
            @input="onChange"
            @focus="onFocus"
            @blur="onBlur" />
      </template>
      <template v-else-if="isInput">
        <BaseValidInput ref="input"
            v-validate="validationRule"
            :data-vv-name="uid"
            :data-vv-scope="validateScope"
            :data-vv-as="properties.validateAs"
            :error-msg="errorMessage"
            :popover-error-msg="properties.popoverErrorMsg"
            :name="name"
            :value="value"
            :placeholder="properties.placeholder"
            :rows="properties.rows"
            :auto-complete="properties.autoComplete"
            :size="size"
            :show-password="properties.password"
            type="text"
            @input="onChange"
            @focus="onFocus"
            @blur="onBlur">
          <BaseCopyButton v-if="properties.showCopyButton && !properties.password"
              slot="append"
              :text="value" />
        </BaseValidInput>
      </template>
      <template v-else-if="isTag">
        <div v-if="properties.path != null"
            class="tag">
          <el-tag v-for="(tag, index) in properties.path(displayItem)"
              :key="index"
              :class="tag.class"
              :type="tag.type"
              :size="properties.size">
            {{ tag.label }}
          </el-tag>
        </div>
      </template>
      <template v-else-if="isLink">
        <router-link :to="{ path: linkPath || null }">
          <template v-if="properties.onLinkClick !== null">
            <span ref="input"
                @click="onLinkClick()"> {{ linkDisplayValue }} </span>
          </template>
          <template v-else>
            {{ linkDisplayValue }}
          </template>
        </router-link>
      </template>
      <template v-else-if="isFunction">
        <template v-if="isLineBreak">
          <span class="line-break">{{ functionDisplayValue }}</span>
        </template>
        <template v-else>
          {{ functionDisplayValue }}
        </template>
      </template>
      <template v-else-if="isFixed">
        <span>{{ properties.path }}</span>
      </template>
      <template v-else-if="isCell">
        <div :class="{'flex': isCell && properties.horizontal}">
          <template v-for="(val, index) in cellProperty">
            <BaseCell
                ref="input"
                :key="index"
                v-validate="validationRule"
                :data-vv-name="uid"
                :data-vv-scope="validateScope"
                :data-vv-as="val.validateAs"
                :class="[{mt10: needMarginTop(index)}, val.class]"
                :properties="val"
                :editable="val.editable != null ? val.editable : !disabled"
                :display-item="val.displayItem != null ? val.displayItem : displayItem"
                :site-code="val.siteCode"
                :validate-scope="val.validateScope != null ? val.validateScope : validateScope"
                @change="onChange"
                @focus="onFocus"
                @blur="onBlur">
              <span v-if="properties.colsep && index!==properties.properties.length-1"
                  slot="colsep">{{ properties.colsep }}</span>
            </BaseCell>
          </template>
        </div>
        <div v-show="showErrorMessage"
            style="display:block"
            class="is-error-msg">
          {{ errorMessage }}
        </div>
      </template>
      <template v-else>
        <template v-if="isLineBreak">
          <!-- 改行が必要な場合 -->
          <!-- el-table-column にclass を設定した場合、暗黙生成される div 要素の white-space の方が強いため、ここで個別設定する -->
          <!-- span 要素は開始から終了を1行にしてください(改行しないでください。) 改行があると line-break が効いて 表示されるデータが改行された後から表示されてしまいます。 -->
          <span class="line-break">{{ formattedDisplayValue }}</span>
        </template>
        <template v-else>
          <div :class="[{'show-copy-button':showCopyButton}]">
            {{ formattedDisplayValue }}
            <BaseCopyButton v-if="showCopyButton"
                :text="formattedDisplayValue"
                class="copy-button" />
          </div>
        </template>
      </template>
      <slot name="colsep" />
    </template>
    <template v-else>
      <template v-if="isTree">
        <el-tree
            :data="treeData"
            :expand-on-click-node="false"
            :node-key="properties.id || 'id'">
          <BaseTreeNodeRadio
              :id="data[properties.id]"
              slot-scope="{ node, data }"
              class="tree_node"
              :label="data[properties.label]"
              :data="data"
              :display-item="displayItem"
              :select-code="properties.selectCode"
              :site-code="siteCode"
              :disabled="disabled" />
        </el-tree>
      </template>
      <template v-else-if="isImage">
        <template v-if="properties.asyncGetImage">
          <ImageLoader
              :src="asyncImageSrc"
              :width="properties.imageWidth"
              :height="properties.imageHeight" />
        </template>
        <template v-else>
          <ImageLoader
              :src="imageUrl"
              :width="properties.imageWidth"
              :height="properties.imageHeight" />
        </template>
      </template>
      <template v-else-if="isHTML"
          lang="html">
        <div v-html="formattedDisplayValue" />
      </template>
      <template v-else-if="isHTMLFunction"
          lang="html">
        <div v-html="functionDisplayValue" />
      </template>
      <template v-else-if="isButton">
        <el-button
            ref="input"
            :size="size"
            :type="type"
            :disabled="disabledButton"
            :icon="icon"
            :class="buttonClass"
            @click="onButtonClick">
          {{ properties.label }}
        </el-button>
      </template>
      <template v-else-if="isSelectBox">
        <BaseValidSelect ref="input"
            :data-vv-name="uid"
            :data-vv-scope="validateScope"
            :data-vv-as="properties.validateAs"
            :error-msg="errorMessage"
            :popover-error-msg="properties.popoverErrorMsg"
            :name="name"
            :value="value"
            :filterable="properties.filterable"
            :options="selectOptions"
            :placeholder="properties.placeholder"
            :size="size"
            :multiple="properties.multiple"
            :clearable="properties.clearable"
            @input="onChange"
            @focus="onFocus"
            @blur="onBlur" />
      </template>
      <template v-else-if="isMultiSelectTag">
        <div class="multi-select-tag">
          <el-tag v-for="(tag, index) in multiSelectCodeNames"
              :key="index"
              type="info">
            {{ tag.name }}
          </el-tag>
        </div>
      </template>
      <template v-else-if="isRadioButton">
        <BaseValidRadio
            :name="name"
            :value="value"
            :radio-list="selectOptions"
            :disabled="disabled"
            button
            @input="onChange" />
      </template>
      <template v-else-if="isCheckBox">
        <BaseValidCheckbox
            ref="input"
            :name="name"
            :class="checkBoxClass"
            :value="value"
            :check-list="checkBoxOptions"
            :disabled="disabled"
            button
            @input="onChange" />
      </template>
      <template v-else-if="isDate">
        <BaseValidDatePicker ref="input"
            :data-vv-name="uid"
            :data-vv-scope="validateScope"
            :data-vv-as="properties.validateAs"
            :error-msg="errorMessage"
            :popover-error-msg="properties.popoverErrorMsg"
            :name="name"
            :value="value"
            :placeholder="properties.placeholder"
            :start-placeholder="properties.placeholder"
            :end-placeholder="properties.placeholder"
            :type="properties.elementType"
            @input="onChange"
            @focus="onFocus"
            @blur="onBlur" />
      </template>
      <template v-else-if="isInputNumber">
        <BaseValidNumber ref="input"
            :data-vv-name="uid"
            :data-vv-scope="validateScope"
            :data-vv-as="properties.validateAs"
            :error-msg="errorMessage"
            :popover-error-msg="properties.popoverErrorMsg"
            :name="name"
            :value="value"
            :placeholder="properties.placeholder"
            :precision="properties.precision"
            :size="size"
            @input="onChange"
            @focus="onFocus"
            @blur="onBlur" />
      </template>
      <template v-else-if="isTextArea">
        <BaseValidInput ref="input"
            :data-vv-name="uid"
            :data-vv-scope="validateScope"
            :data-vv-as="properties.validateAs"
            :error-msg="errorMessage"
            :popover-error-msg="properties.popoverErrorMsg"
            :name="name"
            :value="value"
            :placeholder="properties.placeholder"
            :rows="properties.rows"
            :auto-complete="properties.autoComplete"
            :autosize="properties.autosize"
            :disabled="disabled"
            type="textarea"
            @input="onChange"
            @focus="onFocus"
            @blur="onBlur" />
      </template>
      <template v-else-if="isInput">
        <BaseValidInput ref="input"
            :data-vv-name="uid"
            :data-vv-scope="validateScope"
            :data-vv-as="properties.validateAs"
            :error-msg="errorMessage"
            :popover-error-msg="properties.popoverErrorMsg"
            :name="name"
            :value="value"
            :placeholder="properties.placeholder"
            :rows="properties.rows"
            :auto-complete="properties.autoComplete"
            :size="size"
            :show-password="properties.password"
            type="text"
            @input="onChange"
            @focus="onFocus"
            @blur="onBlur">
          <BaseCopyButton v-if="properties.showCopyButton && !properties.password"
              slot="append"
              :text="value" />
        </BaseValidInput>
      </template>
      <template v-else-if="isTag">
        <div v-if="properties.path != null"
            class="tag">
          <el-tag v-for="(tag, index) in properties.path(displayItem)"
              :key="index"
              :class="tag.class"
              :type="tag.type"
              :size="properties.size">
            {{ tag.label }}
          </el-tag>
        </div>
      </template>
      <template v-else-if="isLink">
        <router-link :to="{ path: linkPath || null }">
          <template v-if="properties.onLinkClick !== null">
            <span ref="input"
                @click="onLinkClick()"> {{ linkDisplayValue }} </span>
          </template>
          <template v-else>
            {{ linkDisplayValue }}
          </template>
        </router-link>
      </template>
      <template v-else-if="isFunction">
        <template v-if="isLineBreak">
          <span class="line-break">{{ functionDisplayValue }}</span>
        </template>
        <template v-else>
          {{ functionDisplayValue }}
        </template>
      </template>
      <template v-else-if="isFixed">
        <span>{{ properties.path }}</span>
      </template>
      <template v-else-if="isCell">
        <div :class="{'flex': isCell && properties.horizontal}">
          <template v-for="(val, index) in cellProperty">
            <BaseCell
                ref="input"
                :key="index"
                :data-vv-name="uid"
                :data-vv-scope="validateScope"
                :data-vv-as="val.validateAs"
                :class="[{mt10: needMarginTop(index)}, val.class]"
                :properties="val"
                :editable="val.editable != null ? val.editable : !disabled"
                :display-item="val.displayItem != null ? val.displayItem : displayItem"
                :site-code="val.siteCode"
                :validate-scope="val.validateScope != null ? val.validateScope : validateScope"
                @change="onChange"
                @focus="onFocus"
                @blur="onBlur">
              <span v-if="properties.colsep && index!==properties.properties.length-1"
                  slot="colsep">{{ properties.colsep }}</span>
            </BaseCell>
          </template>
        </div>
        <div v-show="showErrorMessage"
            style="display:block"
            class="is-error-msg">
          {{ errorMessage }}
        </div>
      </template>
      <template v-else>
        <template v-if="isLineBreak">
          <!-- 改行が必要な場合 -->
          <!-- el-table-column にclass を設定した場合、暗黙生成される div 要素の white-space の方が強いため、ここで個別設定する -->
          <!-- span 要素は開始から終了を1行にしてください(改行しないでください。) 改行があると line-break が効いて 表示されるデータが改行された後から表示されてしまいます。 -->
          <span class="line-break">{{ formattedDisplayValue }}</span>
        </template>
        <template v-else>
          <div :class="[{'show-copy-button':showCopyButton}]">
            {{ formattedDisplayValue }}
            <BaseCopyButton v-if="showCopyButton"
                :text="formattedDisplayValue"
                class="copy-button" />
          </div>
        </template>
      </template>
      <slot name="colsep" />
    </template>
  </div>
</template>

<style scoped>
.el-tag {
  font-weight: normal
}
.multi-select-tag .el-tag:not(:last-child) {
  margin-right: 5px;
}
.flex {
  display: flex;
}
th.required:after {
  content: " *";
  color: #ff5a5a;
  font-size: 85%;
}
.flex > :not(:last-child) {
  margin-right: 5px;
}
.visitedlinkNoChangeColor a {
  color: inherit;
}

.multiLineTextBox[attr_disabled=true]:not(.is-error-msg) {
    height: 115px;
    overflow-y: auto;
    border-style: solid;
    border-width: thin;
    padding-left: 5px;
    padding-right: 5px;
    border-radius: 4px;
    background-color: #FFFFFF;
}

</style>

<script>
import _ from 'lodash'
import Vue from 'vue'
import x from '@/components/mixins/mixin'

import BaseValidSelect from '@/components/atoms/BaseValidSelect'
import BaseValidRadio from '@/components/atoms/BaseValidRadio'
import BaseValidCheckbox from '@/components/atoms/BaseValidCheckbox'
import BaseValidDatePicker from '@/components/atoms/BaseValidDatePicker'
import BaseValidNumber from '@/components/atoms/BaseValidNumber'
import BaseValidInput from '@/components/atoms/BaseValidInput'

import BaseTreeNodeRadio from '@/components/molecules/BaseTreeNodeRadio'
import ImageLoader from '@/components/molecules/ImageLoader'
import BaseCopyButton from '@/components/molecules/BaseCopyButton'

const customValidationRules = (v) => {
  // カスタマイズ属性の最大文字数
  v.$validator.extend('customMaxLengthValue', {
    getMessage: (name) => `データ量が多すぎます。`,
    validate: (value, args) => {
      return ((args || []).join(',')).length < 6000
    }
  })
}

export default {
  name: 'BaseCell',
  components: {
    BaseValidSelect,
    BaseValidRadio,
    BaseValidCheckbox,
    BaseValidDatePicker,
    BaseValidNumber,
    BaseValidInput,
    BaseTreeNodeRadio,
    ImageLoader,
    BaseCopyButton
  },
  inject: ['parentValidator'],
  mixins: [x],
  props: {
    properties: {
      type: Object,
      required: true,
      default: () => {
        return {}
      }
    },
    displayItem: {
      type: Object,
      required: true,
      default: () => {
        return {}
      }
    },
    editable: {
      type: Boolean,
      default: false
    },
    siteCode: {
      type: String,
      default: null
    },
    validateScope: {
      type: String,
      default: null
    }
  },
  asyncComputed: {
    async asyncImageSrc () {
      if (this.isImage && this.properties.asyncGetImage) {
        if (typeof this.properties.imageDataGetAsync === 'function') {
          const imageData = await this.properties.imageDataGetAsync(this.displayItem)
          return imageData
        }
      }
      return null
    }
  },
  computed: {
    uid () {
      return this._uid.toString()
    },
    name () {
      return this.properties.name || this.properties.path
    },
    value () {
      return this.$_mixin_getData(this.displayItem, this.properties.path)
    },
    displayValue () {
      return this.$_mixin_getData(this.displayItem, this.properties.path, this.properties.colsep, this.properties.codeName, { siteCode: this.siteCode })
    },
    formattedDisplayValue () {
      return Vue.filter('formatter')(
        this.displayValue,
        this.properties.type,
        { dateFormat: this.properties.dateFormat,
          fractionDigits: this.properties.fractionDigits
        })
    },
    functionDisplayValue () {
      return Vue.filter('formatter')(
        this.$_mixin_executeFunction(this.displayItem, this.properties.path || (() => {})),
        this.properties.type,
        { dateFormat: this.properties.dateFormat,
          fractionDigits: this.properties.fractionDigits
        })
    },
    multiSelectCodeNames () {
      return _.filter(this.selectOptions, (val) => _.includes(this.value, val.code))
    },
    linkDisplayValue () {
      if (typeof this.properties.path === 'function') {
        return this.functionDisplayValue
      } else {
        return this.displayValue
      }
    },
    linkPath () {
      return this.properties.linkPath + this.$_mixin_getData(this.displayItem, this.properties.linkKey, '&')
    },
    show () {
      return !this.properties.funcId || this.$isFunctionShown(this.properties.funcId)
    },
    disabled () {
      return (this.properties.funcId && this.$isFunctionDisabled(this.properties.funcId)) ||
        (!this.isTree && typeof this.properties.path === 'function') ||
        !this.editable ||
        (typeof this.properties.editableCondition === 'function' ? !this.properties.editableCondition(this.displayItem, this.properties.path) : false)
    },
    disabledButton () {
      return typeof this.properties.disabled === 'function' ? !this.properties.disabled(this.displayItem, this.properties.path) : this.properties.disabled
    },
    size () {
      return this.properties.size || 'mini'
    },
    type () {
      return this.properties.type
    },
    icon () {
      return this.properties.icon
    },
    imageUrl () {
      return typeof this.properties.path === 'function'
        ? this.$_mixin_executeFunction(this.displayItem, this.properties.path)
        : this.$_mixin_getData(this.displayItem, this.properties.path)
    },
    isTree () {
      return this.properties.elementType === ELEMENT_TYPE.TREE
    },
    isImage () {
      return this.properties.elementType === ELEMENT_TYPE.IMAGE
    },
    isHTML () {
      return this.properties.elementType === ELEMENT_TYPE.HTML
    },
    isHTMLFunction () {
      return this.properties.elementType === ELEMENT_TYPE.HTML_FUNCTION
    },
    isButton () {
      return this.properties.elementType === ELEMENT_TYPE.BUTTON
    },
    isSelectBox () {
      return !this.disabled && this.properties.elementType === ELEMENT_TYPE.SELECT_BOX
    },
    isMultiSelectTag () {
      return this.disabled && this.properties.elementType === ELEMENT_TYPE.SELECT_BOX && this.properties.multiple
    },
    isRadioButton () {
      return this.properties.elementType === ELEMENT_TYPE.RADIO_BUTTON
    },
    isCheckBox () {
      return this.properties.elementType === ELEMENT_TYPE.CHECK_BOX
    },
    isDate () {
      return !this.disabled &&
        ([ELEMENT_TYPE.YEAR, ELEMENT_TYPE.DATE, ELEMENT_TYPE.DATE_RANGE, ELEMENT_TYPE.DATE_TIME, ELEMENT_TYPE.DATE_TIME_RANGE, ELEMENT_TYPE.DATE_MILLI_SECONDS, ELEMENT_TYPE.DATETIME_MILLI_SECONDS].includes(this.properties.elementType))
    },
    isInputNumber () {
      return !this.disabled && this.properties.elementType === ELEMENT_TYPE.INPUT_NUMBER
    },
    isInput () {
      return !this.disabled && this.properties.elementType === ELEMENT_TYPE.INPUT
    },
    isTextArea () {
      return !this.disabled && this.properties.elementType === ELEMENT_TYPE.INPUT_TEXT_AREA
    },
    isTag () {
      return this.properties.elementType === ELEMENT_TYPE.TAG
    },
    isLink () {
      return this.properties.elementType === ELEMENT_TYPE.LINK &&
        (!(this.properties.funcId && this.$isFunctionDisabled(this.properties.funcId)) ||
        this.properties.linkKey != null)
    },
    isFunction () {
      return this.properties.elementType === ELEMENT_TYPE.FUNCTION
    },
    isCell () {
      return this.properties.elementType === ELEMENT_TYPE.CELL
    },
    isFixed () {
      return this.properties.elementType === ELEMENT_TYPE.FIXED_TEXT
    },
    isLineBreak () {
      return this.properties.lineBreak
    },
    treeData () {
      if (typeof this.properties.path === 'function') {
        return this.$_mixin_executeFunction(this.displayItem, this.properties.path)
      } else {
        return this.properties.path
      }
    },
    selectOptions () {
      return typeof this.properties.selectCode === 'function'
        ? this.properties.selectCode(this.displayItem)
        : this.$_mixin_selectOptions(this.properties.selectCode, { siteCode: this.siteCode })
    },
    checkBoxOptions () {
      if (this.properties.single) {
        return [{ code: '1', name: this.properties.label, trueLabel: 1, falseLabel: 0 }]
      } else {
        return typeof this.properties.selectCode === 'function'
          ? this.properties.selectCode(this.displayItem)
          : this.$_mixin_selectOptions(this.properties.selectCode, { siteCode: this.siteCode })
      }
    },
    validationRule () {
      if (this.disabledValidation) {
        return ''
      }
      return typeof this.properties.validateRule === 'function'
        ? this.properties.validateRule(this.displayItem)
        : this.properties.validateRule
    },
    needValidation () {
      return this.properties.validateRule != null
    },
    disabledValidation () {
      return typeof this.properties.disabledValidation === 'function'
        ? this.properties.disabledValidation(this.displayItem)
        : this.properties.disabledValidation
    },
    errorMessage () {
      return this.errors.first(this.uid, this.validateScope)
    },
    showErrorMessage () {
      return !this.disabled && this.errorMessage
    },
    checkBoxClass () {
      return {
        'horizontal-single-checkbox': this.properties.single,
        'checked-noninput': this.disabled
      }
    },
    buttonClass () {
      return typeof this.properties.buttonClass === 'function'
        ? this.properties.buttonClass(this.displayItem)
        : this.properties.buttonClass
    },
    showCopyButton () {
      return this.formattedDisplayValue && this.properties.showCopyButton
    },
    cellProperty () {
      if (typeof this.properties.properties === 'function') {
        return this.properties.properties(this.displayItem, this.displayValue, this.editable)
      } else {
        return this.properties.properties
      }
    }
  },
  watch: {
    selectOptions (val) {
      if (this.properties.autoSelectInSingleOption && val.length === 1 && this.isSelectBox) {
        const oldValue = this.value
        if (this.properties.multiple) {
          this.$_mixin_updateValue([(this.selectOptions[0] || {}).code || ''], this.displayItem, this.properties.path, this.properties.type === 'Number')
        } else {
          this.$_mixin_updateValue((this.selectOptions[0] || {}).code, this.displayItem, this.properties.path, this.properties.type === 'Number')
        }
        this.$emit('change', this.value, this.displayItem, this.properties.path, oldValue)
      }
    }
  },
  created () {
    this.$validator = this.parentValidator
    customValidationRules(this)
  },
  methods: {
    onChange (event) {
      const oldValue = this.value
      if (this.isCell) {
        if (this.needValidation) {
          const target = _.chain([this.validateScope, this.uid])
            .filter(val => val != null)
            .join('.')
            .value()
          this.$validator.validate(target)
        }
        return
      }
      if (this.properties.change && typeof this.properties.change === 'function') {
        this.properties.change(event, oldValue)
      }
      this.$_mixin_updateValue(event, this.displayItem, this.properties.path, this.properties.type === 'Number')
      this.$emit('change', event, this.displayItem, this.properties.path, oldValue)
    },
    onFocus (event) {
      this.$emit('focus', event, this.displayItem, this.properties.path)
    },
    onBlur (event, before, after) {
      this.$emit('blur', event, this.displayItem, this.properties.path, before, after)
    },
    onLinkClick () {
      this.properties.onLinkClick(this.displayItem)
    },
    onButtonClick (event) {
      if (this.properties.click && typeof this.properties.click === 'function') {
        this.properties.click(event, this.displayItem, this.displayValue)
      }
    },
    needMarginTop (index) {
      return !this.properties.horizontal && index > 0
    },
    // 親からインプット要素をフォーカスするために使われる
    focus: function () {
      this.$refs.input.focus(...arguments)
    }
  }
}
export const ELEMENT_TYPE = Object.freeze({
  TEXT: 'text',
  FIXED_TEXT: 'fixed',
  LINK: 'link',
  FUNCTION: 'Function',
  TAG: 'tag',
  INPUT: 'String',
  INPUT_NUMBER: 'Number',
  INPUT_TEXT_AREA: 'textarea',
  SELECT_BOX: 'select',
  RADIO_BUTTON: 'radio',
  CHECK_BOX: 'checkbox',
  YEAR: 'year',
  DATE: 'date',
  DATE_RANGE: 'daterange',
  DATE_TIME: 'datetime',
  DATE_TIME_RANGE: 'datetimerange',
  DATE_MILLI_SECONDS: 'timestamp_date',
  DATETIME_MILLI_SECONDS: 'timestamp_datetime',
  TREE: 'tree',
  IMAGE: 'image',
  BUTTON: 'button',
  HTML: 'html',
  HTML_FUNCTION: 'html_function',
  CELL: 'cell'
})
</script>



// WEBPACK FOOTER //
// src/components/molecules/BaseCell.vue